import { useState } from 'react';
import { submitEarlyAccess } from '../utils/supabase';
import { trackFormSubmission, trackFormError } from '../utils/analytics';

/**
 * Custom hook for handling early access signup form
 * @returns {Object} Form state and handlers
 */
export const useSignupForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email_address: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Whether email is valid
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate form fields
   * @returns {{isValid: boolean, errors: Object}} Validation result
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email_address.trim()) {
      newErrors.email_address = 'Email is required';
    } else if (!validateEmail(formData.email_address)) {
      newErrors.email_address = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  };

  /**
   * Handle input change
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   * @param {Object} additionalData - Additional form data (e.g., selected_gig)
   * @param {string} formName - Name of the form for analytics (optional)
   */
  const handleSubmit = async (e, additionalData = {}, formName = 'Early Access Signup') => {
    e.preventDefault();

    const validationResult = validateForm();
    if (!validationResult.isValid) {
      // Track validation errors
      const errorFields = Object.keys(validationResult.errors);
      if (errorFields.length > 0) {
        trackFormError(formName, `validation_error: ${errorFields.join(', ')}`);
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Merge formData with any additional data (like gig selection)
      const submissionData = { ...formData, ...additionalData };
      const { error } = await submitEarlyAccess(submissionData);

      if (error) {
        setSubmitStatus('error');
        setErrors({ submit: error.message || 'Failed to submit. Please try again.' });
        // Track form error
        trackFormError(formName, error.message || 'submission_error');
      } else {
        setSubmitStatus('success');
        setFormData({ name: '', email_address: '' });
        // Track successful form submission
        trackFormSubmission(formName, submissionData);
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      // Track unexpected error
      trackFormError(formName, 'unexpected_error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset form state
   */
  const resetForm = () => {
    setFormData({ name: '', email_address: '' });
    setErrors({});
    setSubmitStatus(null);
  };

  return {
    formData,
    errors,
    isSubmitting,
    submitStatus,
    handleChange,
    handleSubmit,
    resetForm,
  };
};
